import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {THEME} from "@/enums/profile";
import {PieDataType} from "@/utils/common/charts";
import {GetTheme} from "@/utils/common/general";
import {FormatNumber} from "@/utils/common/generic";
import {SmartT} from "@/utils/common/translations";
import {ResponsivePie} from "@nivo/pie";

type PieMargin = {
   top?: number;
   right?: number;
   bottom?: number;
   left?: number;
};

type PieProps = {
   data: PieDataType[];
   arcLabel?: (e: any) => string;
   themeOverride?: THEME;
   innerRadius?: number; // 0-1, higher values = thinner donut
   enableArcLinkLabels?: boolean;
   enableArcLabels?: boolean;
   padAngle?: number;
   margin?: PieMargin;
};

export const Pie = ({
   data,
   arcLabel,
   themeOverride,
   innerRadius = 0.5,
   enableArcLinkLabels = true,
   enableArcLabels = true,
   padAngle = 0.7,
   margin,
}: PieProps) => {
   const {t} = useTranslation("stats");
   const {t: ct} = useTranslation("common");

   const theme = themeOverride || GetTheme();
   const dataFiltered = data.filter((d) => d.value > 0);

   const defaultMargin = enableArcLinkLabels
      ? {top: 30, right: 60, bottom: 30, left: 80}
      : {top: 25, right: 25, bottom: 25, left: 25};

   return (
      <ResponsivePie
         data={dataFiltered}
         margin={margin ?? defaultMargin}
         innerRadius={innerRadius}
         padAngle={padAngle}
         activeOuterRadiusOffset={8}
         borderColor={{
            from: "color",
            modifiers: [["darker", 0.2]],
         }}
         tooltip={(d: any) => (
            <div className={clsx("p-2", "bg-panel", "shadow-lg", "rounded-lg", "border")}>
               {SmartT(d.datum?.id as string, t, {
                  value: FormatNumber(d.datum?.value, ct, 2),
               })}
            </div>
         )}
         sortByValue={true}
         arcLabel={arcLabel || undefined}
         enableArcLabels={enableArcLabels}
         enableArcLinkLabels={enableArcLinkLabels}
         arcLinkLabel="label"
         arcLinkLabelsSkipAngle={10}
         arcLinkLabelsTextColor={theme === THEME.LIGHT ? "#333333" : "#ffffff"}
         arcLinkLabelsThickness={2}
         arcLinkLabelsColor={{from: "color"}}
         arcLinkLabelsDiagonalLength={10}
         arcLinkLabelsStraightLength={5}
         arcLabelsSkipAngle={10}
         arcLabelsTextColor={{
            from: "color",
            modifiers: [["darker", 2]],
         }}
         colors={({data}) => data?.color || "hsl(0, 0%, 0%)"}
      />
   );
};
